import { PreparedVariation, VariantMatrix } from '@telialt/models/src/EshopModel';
import { getFirstInStockLeaf, getFirstLeaf, toPreparedVariations } from '@telialt/utils/src/eshopUtils';
import { isNullOrUndefined } from '@telialt/utils/src/helpers';

import { City, Salon, WeekendValue } from './model';

export const hasPriority = (city: City): boolean => !isNullOrUndefined(city.topCityPriority);

export const getSortedCities = (cities: City[]) => {
    return [
        ...cities.filter((city) => hasPriority(city)).sort((a, b) => a.topCityPriority - b.topCityPriority),
        ...cities
            .filter((city) => !hasPriority(city))
            .sort((a, b) =>
                a.city.localeCompare(b.city, 'lt', {
                    sensitivity: 'accent',
                }),
            ),
    ];
};

export const getEnrichedCities = (cities: City[]) => {
    return cities.map((city) => ({ ...city, name: city.city, count: city.salons.length }));
};

type FilterParams = {
    cities: City[];
    weekendFilter: Record<WeekendValue, boolean>;
    variationFilter?: PreparedVariation;
};

export const getFilteredCities = ({ cities, weekendFilter, variationFilter }: FilterParams) => {
    return cities.reduce((acc, city) => {
        const salons = city.salons.filter((salon) => {
            if ((weekendFilter.saturday && !salon.openOnSaturday) || (weekendFilter.sunday && !salon.openOnSunday)) {
                return false;
            }

            if (
                variationFilter &&
                !salon.productColorVariants.find((colorVariant) => colorVariant.id === variationFilter.code)
            ) {
                return false;
            }

            return true;
        });

        acc.push({ ...city, salons });

        return acc;
    }, []);
};

export const getPriorityCities = (cityData: City[]) => {
    return cityData.filter((city) => hasPriority(city));
};

export const findNearestSalon = (allCities: City[]) => {
    return new Promise<Salon>((resolve, reject) => {
        // User-initiated "find nearest store" flow; browser handles consent prompt.
        // eslint-disable-next-line sonarjs/no-intrusive-permissions
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const userLocation = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);

                const allSalons = allCities.reduce<Salon[]>((acc, city) => {
                    return [...acc, ...city.salons];
                }, []);

                const salon = allSalons.reduce((previous, current) => {
                    const previousDistance = google.maps.geometry.spherical.computeDistanceBetween(
                        userLocation,
                        new google.maps.LatLng(previous.lat, previous.lng),
                    );
                    const currentDistance = google.maps.geometry.spherical.computeDistanceBetween(
                        userLocation,
                        new google.maps.LatLng(current.lat, current.lng),
                    );

                    return currentDistance < previousDistance ? current : previous;
                });

                resolve(salon);
            },
            (error) => reject(error),
        );
    });
};

export const getFirstProductVariation = (variantMatrix?: VariantMatrix[]) => {
    if (!variantMatrix) {
        return undefined;
    }
    const preparedVariations = toPreparedVariations(variantMatrix);

    return getFirstInStockLeaf(preparedVariations) || getFirstLeaf(preparedVariations);
};
